import { ShapeElement } from "../Components/Elements/Graphics/ShapeElement";
import { TextElement } from "../Components/Elements/Texts/TextElement";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";

/**
 * @class DebugData
 * @author Bohuslav Chlubna
 * 30.6.2023
 * todo xml elements template
 */

enum showData {
    Off = 0,
    ShowInPixi = 1,
    ShowOnScreen = 2,
}
export class DebugData {
    private debugMode: showData = showData.Off;
    // private debugMode: showData = showData.ShowInPixi;
    // private debugMode: showData = showData.ShowOnScreen;

    private debugDataContainer: BaseElement;
    private border: ShapeElement;
    private textInfoAll: TextElement;
    private textInfoLeft: TextElement;
    private textInfoRight: TextElement;
    private pointPivot: ShapeElement;
    private pointPosition: ShapeElement;
    private created: boolean = false;
    private debugVariables: Array<string> = [];
    private offsetX: number = 0;
    private offsetY: number = 0;

    /* current node attributes
    x, y, alpha, visible, style, case, pivot_x, pivot_y, scale_x, scale_y, anchor_x, anchor_y, parent
    all (or true) - show all atributes of element
    hide - hide table and show only in Pixi
     */

    /*
    <CS_DebugDefault Class="ContainerStyle">
        <Bg Class="ShapeElement" X="100" Y="100" Width="170" Height="16" Color="000000" Alpha="0.6" LineColor="0xFF000" LineWidth="2"/>
        <PivotDot Class="ShapeElement" X="0" Y="0" Width="9" Height="9" Color="00FFFF" Alpha="0.6" LineColor="00FF00" LineWidth="2"/>
        <PositionDot Class="ShapeElement" X="0" Y="0" Width="7" Height="7" Color="FFFF00" Alpha="0.6"/>
        <TextLeft Class="TextElement" Size="13" X="115" Y="100" Text="parent:" Tint="00FFFF" TextAlign="right"/>
        <TextRight Class="TextElement" Size="13" X="160" Y="100" Text="WinContainer" Tint="FFFF00" TextAlign="left"/>
    </CS_DebugDefault>
     */

    public showDecimals(number: number, decimals?: number): number {
        // default
        let decimalsCount = 3;
        if (decimals !== null) {
            if (decimals === 0) {
                return Math.round(number);
            } else {
                decimalsCount = decimals;
            }
        }
        decimalsCount = Math.pow(10, 3);
        if (decimalsCount > 0) {
            return Math.round(number * decimalsCount) / decimalsCount;
        } else {
            return number;
        }
    }

    public showDebugData(parent: any, node?: Element) {
        if (DEBUG) {
            if (this.debugMode > showData.Off) {
                if (
                    this.debugMode === showData.ShowOnScreen &&
                    node &&
                    node.getAttribute(DebugDataAttributes.DEBUG_DATA_OFFSET)
                ) {
                    let offsets = node.getAttribute(DebugDataAttributes.DEBUG_DATA_OFFSET).split(";");
                    if (offsets.length === 1) {
                        this.offsetX = parseFloat(offsets[0]);
                        this.offsetY = parseFloat(offsets[0]);
                    } else if (offsets.length === 2) {
                        this.offsetX = parseFloat(offsets[0]);
                        this.offsetY = parseFloat(offsets[1]);
                    }
                }
                if (node && node.getAttribute(DebugDataAttributes.DEBUG_DATA)) {
                    let variables = node.getAttribute(DebugDataAttributes.DEBUG_DATA).toLowerCase().split(";");
                    this.updateDebugData(parent, variables);
                } else if (this.created) {
                    this.updateDebugData(parent, null);
                }
            }
        }
    }

    public updateDebugData(parent: any, showedParameters: Array<string>) {
        if (DEBUG) {
            if (this.debugMode > showData.Off) {
                if (parent) {
                    let allSeparator: string = ":       ";

                    let textStringLeft: string = "name: " + "\n";
                    let textStringRight: string = parent.name + "\n";
                    let textStringAll: string = "name" + allSeparator + parent.name + "\n";
                    let rowCount: number = 1;

                    let readData: Array<Object> = [];
                    if (typeof parent.parent !== "undefined" && parent.parent !== null) {
                        let nextParent = parent.parent;
                        while (nextParent) {
                            if (nextParent.name !== null && nextParent.name !== "Elements") {
                                readData.unshift({ data: "parent", value: nextParent.name });
                            }
                            if (typeof nextParent.parent !== "undefined" && nextParent.parent !== null) {
                                nextParent = nextParent.parent;
                            } else {
                                nextParent = null;
                            }
                        }
                    }
                    if (typeof parent.style !== "undefined" && typeof parent.style.name !== "undefined") {
                        readData.push({ data: "style", value: parent.style.name });
                    }
                    if (typeof parent.position !== "undefined") {
                        if (typeof parent.position.x !== "undefined") {
                            readData.push({ data: "x", value: parent.position.x });
                        }
                        if (typeof parent.position.y !== "undefined") {
                            readData.push({ data: "y", value: parent.position.y });
                        }
                    }
                    if (typeof parent.scale !== "undefined") {
                        if (typeof parent.scale.x !== "undefined") {
                            readData.push({ data: "scale_x", value: this.showDecimals(parent.scale.x) });
                        }
                        if (typeof parent.scale.y !== "undefined") {
                            readData.push({ data: "scale_y", value: this.showDecimals(parent.scale.y) });
                        }
                    }
                    if (typeof parent.anchor !== "undefined") {
                        if (parent.anchor.x !== "undefined") {
                            readData.push({ data: "anchor_x", value: parent.anchor.x });
                        }
                        if (parent.anchor.y !== "undefined") {
                            readData.push({ data: "anchor_y", value: parent.anchor.y });
                        }
                    }
                    if (typeof parent.pivot !== "undefined") {
                        if (typeof parent.pivot.x !== "undefined") {
                            readData.push({ data: "pivot_x", value: parent.pivot.x });
                        }
                        if (typeof parent.pivot.y !== "undefined") {
                            readData.push({ data: "pivot_y", value: parent.pivot.y });
                        }
                    }
                    if (typeof parent.visible !== "undefined") {
                        readData.push({ data: "visible", value: parent.visible });
                    }
                    if (typeof parent.alpha !== "undefined") {
                        readData.push({ data: "alpha", value: this.showDecimals(parent.alpha) });
                    }
                    if (typeof parent.rotation !== "undefined") {
                        readData.push({ data: "rotation", value: this.showDecimals(parent.rotation) });
                    }
                    if (typeof parent.width !== "undefined") {
                        readData.push({ data: "width", value: this.showDecimals(parent.width) });
                    }
                    if (typeof parent.height !== "undefined") {
                        readData.push({ data: "height", value: this.showDecimals(parent.width) });
                    }
                    if (typeof parent.color !== "undefined") {
                        readData.push({ data: "color", value: parent.color.toString(16).toUpperCase() });
                    }
                    if (typeof parent.tint !== "undefined") {
                        readData.push({ data: "tint", value: parent.tint.toString(16).toUpperCase() });
                    }
                    if (typeof parent.animationSpeed !== "undefined") {
                        readData.push({ data: "speed", value: parent.animationSpeed });
                    }
                    if (typeof parent.playing !== "undefined") {
                        readData.push({ data: "playing", value: parent.playing });
                    }
                    if (typeof parent.case !== "undefined") {
                        readData.push({ data: "case", value: parent.case });
                    }
                    // take new parameters from node
                    if (showedParameters) {
                        this.debugVariables = showedParameters;
                    }
                    let longestText: number = 0;
                    // with goToCase, there is no node parameters, so show all of them; or put "all" in parameter
                    if (
                        this.debugVariables.length === 0 ||
                        this.debugVariables.indexOf("all") > -1 ||
                        this.debugVariables.indexOf("true") > -1
                    ) {
                        readData.forEach((object) => {
                            let label: string = object["data"];
                            let value: string = object["value"];
                            textStringLeft += label + ": " + "\n";
                            textStringRight += value + "\n";
                            textStringAll += label + allSeparator + value + "\n";
                            rowCount++;
                            if (object["data"].length > longestText) {
                                longestText = object["data"].length;
                            }
                        });
                    } else {
                        readData.forEach((object) => {
                            if (this.debugVariables.indexOf(object["data"].toLowerCase()) > -1) {
                                let label: string = object["data"];
                                let value: string = object["value"];
                                textStringLeft += label + ": " + "\n";
                                textStringRight += value + "\n";
                                textStringAll += label + allSeparator + value + "\n";
                                rowCount++;
                                if (object["data"].length > longestText) {
                                    longestText = object["data"].length;
                                }
                            }
                        });
                    }

                    if (!this.created) {
                        this.border = new ShapeElement();
                        this.border.name = DebugDataAttributes.DEBUG_DATA + "_BG";
                        this.border.adjust({
                            color: 0x000000,
                            width: 200,
                            lineColor: 0x00ff00,
                            lineWidth: 1,
                            alpha: 0.7,
                            x: 1,
                            y: 1,
                        });

                        this.textInfoLeft = new TextElement();
                        this.textInfoLeft.name = DebugDataAttributes.DEBUG_DATA + "_TEXT_LEFT";
                        this.textInfoLeft.style.fill = 0xffffff;
                        this.textInfoLeft.style.align = "right";

                        this.textInfoRight = new TextElement();
                        this.textInfoRight.name = DebugDataAttributes.DEBUG_DATA + "_TEXT_RIGHT";
                        this.textInfoRight.style.fill = 0xffff00;
                        this.textInfoRight.style.align = "left";

                        this.textInfoRight.style.fontSize = this.textInfoLeft.style.fontSize = 13;
                        this.textInfoRight.style.lineHeight = this.textInfoLeft.style.lineHeight = 15;

                        this.pointPosition = new ShapeElement();
                        this.pointPosition.name = DebugDataAttributes.DEBUG_DATA + "_POSITION";
                        this.pointPosition.adjust({
                            color: 0x7aff4a,
                            width: 11,
                            height: 11,
                            lineWidth: 1,
                            lineColor: 0x27336e,
                            alpha: 0.8,
                            x: parent.position.x,
                            y: parent.position.y,
                        });

                        this.pointPivot = new ShapeElement();
                        this.pointPivot.name = DebugDataAttributes.DEBUG_DATA + "_PIVOT";
                        this.pointPivot.adjust({
                            color: 0x4a68ff,
                            width: 8,
                            height: 8,
                            lineWidth: 1,
                            lineColor: 0x27336e,
                            alpha: 0.8,
                            x: parent.pivot.x,
                            y: parent.pivot.y,
                        });

                        this.textInfoLeft.x = 72;
                        this.textInfoLeft.y = 1;
                        this.textInfoRight.x = 72;
                        this.textInfoRight.y = 1;

                        this.textInfoAll = new TextElement();
                        this.textInfoAll.visible = false;
                        this.textInfoAll.name = DebugDataAttributes.DEBUG_DATA + "_INFO";

                        this.debugDataContainer = new BaseElement();
                        this.debugDataContainer.name = DebugDataAttributes.DEBUG_DATA + "_CON";
                        this.debugDataContainer.position.x = parent.position.x - parent.pivot.x + this.offsetX;
                        this.debugDataContainer.position.y = parent.position.y - parent.pivot.y + this.offsetY;
                        this.debugDataContainer.addChild(this.border);
                        this.debugDataContainer.addChild(this.textInfoLeft);
                        this.debugDataContainer.addChild(this.textInfoRight);
                        parent.addChild(this.textInfoAll);
                        parent.addChild(this.debugDataContainer);
                        parent.addChild(this.pointPosition);
                        parent.addChild(this.pointPivot);

                        if (this.debugMode === showData.ShowInPixi || this.debugVariables.indexOf("hide") > -1) {
                            this.debugDataContainer.visible = false;
                            this.pointPivot.visible = false;
                            this.pointPosition.visible = false;
                        }

                        this.created = true;
                    }
                    if (this.border && this.textInfoLeft) {
                        this.textInfoLeft.text = textStringLeft;
                        this.textInfoRight.text = textStringRight;
                        this.textInfoAll.text = textStringAll;
                        // left text align
                        this.textInfoLeft.pivot.x = longestText * 3.6 + 22;
                        this.border.adjust({
                            height: rowCount * this.textInfoLeft.style.lineHeight + 2,
                        });
                    }
                }
            }
        }
    }

    public clone(element: any) {
        if (DEBUG) {
            if (this.debugMode > showData.Off) {
                if (this.created) {
                    if (this.debugMode === showData.ShowOnScreen) {
                        if (this.offsetX !== 0) {
                            element._debugData.offsetX = this.offsetX;
                        }
                        if (this.offsetY !== 0) {
                            element._debugData.offsetY = this.offsetY;
                        }
                    }
                    if (this.debugVariables) {
                        element._debugData.updateDebugData(element, this.debugVariables);
                    } else {
                        element._debugData.updateDebugData(element, null);
                    }
                }
            }
        }
    }
}

export const DebugDataAttributes = {
    DEBUG_DATA: "DebugData",
    DEBUG_DATA_OFFSET: "DebugDataOffset",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Utils/DebugData.ts
 **/